import { Box } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type { ReactNode } from 'react';
import { loginPage } from '@/constants/loginPage';

interface LoginBackgroundProps {
  children: ReactNode;
  footer?: ReactNode;
}

export function LoginBackground({ children, footer }: LoginBackgroundProps) {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        bgcolor: 'background.default',
        backgroundImage: isDark
          ? `radial-gradient(circle at 50% 42%, ${alpha(theme.palette.primary.main, 0.2)} 0%, transparent 62%)`
          : 'radial-gradient(circle at 50% 42%, rgba(255, 255, 255, 0.9) 0%, transparent 62%)',
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            width: loginPage.decorationCircleSize,
            height: loginPage.decorationCircleSize,
            transform: 'translate(-50%, -54%)',
            borderRadius: '50%',
            border: `1px dashed ${alpha(theme.palette.divider, isDark ? 0.72 : 0.45)}`,
            opacity: 0.7,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            bottom: { xs: '-8%', sm: '-4%' },
            left: { xs: '-18%', sm: '-6%' },
            width: { xs: 220, sm: 320 },
            height: { xs: 220, sm: 320 },
            borderLeft: `1px solid ${alpha(theme.palette.divider, isDark ? 0.45 : 0.35)}`,
            borderBottom: `1px solid ${alpha(theme.palette.divider, isDark ? 0.45 : 0.35)}`,
            transform: 'rotate(45deg)',
            opacity: 0.5,
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: { xs: '8%', sm: '12%' },
            right: { xs: '-12%', sm: '-4%' },
            width: { xs: 160, sm: 240 },
            height: { xs: 160, sm: 240 },
            borderTop: `1px solid ${alpha(theme.palette.divider, isDark ? 0.4 : 0.3)}`,
            borderRight: `1px solid ${alpha(theme.palette.divider, isDark ? 0.4 : 0.3)}`,
            transform: 'rotate(12deg)',
            opacity: 0.4,
          }}
        />
      </Box>

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: { xs: 4, sm: 6 },
        }}
      >
        {children}
      </Box>

      {footer ? (
        <Box
          sx={{
            position: 'relative',
            zIndex: 1,
            px: 2,
            pb: { xs: 3, sm: 4 },
          }}
        >
          {footer}
        </Box>
      ) : null}
    </Box>
  );
}
